import { RowCrudStatus } from "../../common/types";
import {
  CreditTier,
  CreditTierCreateMutationVariables,
  CreditTierUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";

export type CreditTierValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name_es: string;
  name_en: string;
  name_pt: string;
  monthlyCredits: number;
  pricePerCredit: number;
  pricePerExtraCredit: number;
  description_es: string;
  description_en: string;
  description_pt: string;
};

export type CreditTierErrorsType = {
  name_es?: { message: string; fieldLabel: string };
  name_en?: { message: string; fieldLabel: string };
  name_pt?: { message: string; fieldLabel: string };
  monthlyCredits?: { message: string; fieldLabel: string };
  pricePerCredit?: { message: string; fieldLabel: string };
  pricePerExtraCredit?: { message: string; fieldLabel: string };
  description_es?: { message: string; fieldLabel: string };
  description_en?: { message: string; fieldLabel: string };
  description_pt?: { message: string; fieldLabel: string };
};

export function CreditTierValuesInit(): CreditTierValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name_es: "",
    name_en: "",
    name_pt: "",
    monthlyCredits: 0,
    pricePerCredit: 0,
    pricePerExtraCredit: 0,
    description_es: "",
    description_en: "",
    description_pt: "",
  };
}

export type CreditTierType = CreditTier | CreditTierValuesType;

export function creditTierDisplayId(
  source: Partial<CreditTier>,
  _langStore: LangStore
) {
  return `${source[_langStore.fieldByLang("name") as keyof CreditTierType]}`;
}

export function getCreditTierValues(
  source: CreditTierType,
  _langStore: LangStore,
  _duplicated: boolean = false
): CreditTierValuesType {
  const entityValues = CreditTierValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : creditTierDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name_es = source.name_es;
  entityValues.name_en = source.name_en;
  entityValues.name_pt = source.name_pt;
  entityValues.monthlyCredits = source.monthlyCredits || 0;
  entityValues.pricePerCredit = Number(source.pricePerCredit) || 0;
  entityValues.pricePerExtraCredit = Number(source.pricePerExtraCredit) || 0;
  entityValues.description_es = source.description_es || "";
  entityValues.description_en = source.description_en || "";
  entityValues.description_pt = source.description_pt || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getCreditTierUpdateVariables(
  values: CreditTierValuesType
): CreditTierUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    monthlyCredits: values.monthlyCredits,
    pricePerCredit: values.pricePerCredit,
    pricePerExtraCredit: values.pricePerExtraCredit,
    description_es: blankToNull(values.description_es),
    description_en: blankToNull(values.description_en),
    description_pt: blankToNull(values.description_pt),
  };
}

export function getCreditTierCreateVariables(
  values: CreditTierValuesType
): CreditTierCreateMutationVariables {
  return {
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    monthlyCredits: values.monthlyCredits,
    pricePerCredit: values.pricePerCredit,
    pricePerExtraCredit: values.pricePerExtraCredit,
    description_es: blankToNull(values.description_es),
    description_en: blankToNull(values.description_en),
    description_pt: blankToNull(values.description_pt),
  };
}
